import * as React from 'react';
import Alert from '@material-ui/lab/Alert';
import {
  Grid, Box, TextField, Divider, Card, CardContent,
  Typography,
  CardHeader,
  Paper,
  CircularProgress,
  IconButton,
  MenuItem,
} from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import useSWR from 'swr';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { debounce } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import type { GridColumns } from '@material-ui/data-grid';
import { DataGrid } from '@material-ui/data-grid';
import useEventListener from '../../lib/useEventListener';
import RepairCard from '../metalCollection/RepairCardCarousel';
import RepairInformation from '../metalCollection/RepairInformation';
import RepairJobContainer from '../packer/RepairJobContainer';
import type { IScannedRepair, IRepairItem } from '../marking/MarkingInterfaces';
import RepairRejectLog from './RepairRejectLog';
import ActionMenu from '../global/ActionMenu';

const repairScanTableColumns: GridColumns = [
  {
    field: 'scanBody',
    headerName: 'Body',
    flex: 0.75,
  },
  {
    field: 'scanType',
    headerName: 'Type',
    flex: 0.33,
  },
  {
    field: 'scanLocation',
    headerName: 'Scan Location',
    flex: 1,
  },
  {
    field: 'created',
    headerName: 'Created',
    flex: 1,
  },
  {
    field: 'scannedBy',
    headerName: 'Scanned By',
    flex: 1,
  },
];
interface IScan {
  scanBody: string;
  scanType: string;
  scanLocation: string;
  created: string;
  scannedBy: string;
}

interface IScannedBox {
  boxId: string;
  trackingNumber: string;
  itemCount: number;
}

const Packer: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { repairBarcode } = useParams();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [boxLinkBarcode, setBoxLinkBarcode] = React.useState<string | null>(null);
  const [barcode, setBarcode] = React.useState<string | undefined>(repairBarcode);
  const [repairId, setRepairId] = React.useState<string>();
  const [openRepairProcessing, setOpenRepairProcessing] = React.useState<boolean>(repairBarcode !== undefined);

  const handleActionMenuClick = (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!anchorEl) {
      setBoxLinkBarcode(id);
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setBoxLinkBarcode(null);
    setAnchorEl(null);
  };

  const displayVertIcon = (boxBarcodeParam: string) => (
    <IconButton
      aria-label="more"
      aria-controls="long-menu"
      aria-haspopup="true"
      id="table-kabob"
      onClick={(e) => handleActionMenuClick(e, boxBarcodeParam)}
    >
      <MoreVertIcon />
    </IconButton>
  );
  const repairFetcher = (url: string, repairIdParam: string, packer: boolean) => axios.get<IScannedRepair>(url, {
    params: {
      repairId: repairIdParam,
      isPacker: packer,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      if (e.code === '404') {
        setBarcode(undefined);
        setRepairId(undefined);
        navigate('/repair-detail');
      } else {
        setBarcode(undefined);
        setRepairId(undefined);
        navigate('/repair-detail');
        enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
      }
    });

  const repairScanFetcher = (url: string, barcodeParam: string) => axios.get<IScan[]>(url, {
    params: {
      barcode: barcodeParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const boxFetcher = (url: string, repairIdParam: string) => axios.get<IScannedBox>(url, {
    params: {
      repairId: repairIdParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const boxScanFetcher = (url: string, trackNumParam: string) => axios.get<IScan[]>(url, {
    params: {
      trackingNumber: trackNumParam,
    },
  }).then((res) => res.data)
    .catch((e: AxiosError) => {
      enqueueSnackbar(e?.response?.data, { persist: false, variant: 'error' });
    });

  const getRepairId = (repairBarcodeParam: string) => {
    const repairIdReturn = axios.get<{ repairId:string }>('/Wayfinder/ScanRepair', {
      params: {
        barcode: repairBarcodeParam,
      },
    })
      .then((response) => (response.data.repairId))
      .catch((error: AxiosError) => {
        enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
      });
    return repairIdReturn;
  };

  const openRepair = debounce((barcodeInput: string) => {
    if (barcodeInput !== '') {
      setOpenRepairProcessing(true);
      setBarcode(barcodeInput);
      getRepairId(barcodeInput)
        .then(
          (result) => { if (result) setRepairId(result); },
        )
        .catch(
          (error) => enqueueSnackbar(error, { persist: false, variant: 'error' }),
        )
        .finally(() => setOpenRepairProcessing(false));
    }
  }, 500);

  React.useEffect(() => {
    if (repairBarcode) { openRepair(repairBarcode); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data } = useSWR(repairId ? ['/Packer/GetRepair', repairId, false] : null, (url, repairIdParam, isPacker) => repairFetcher(url, repairIdParam, isPacker));
  const { data: repairScans } = useSWR(barcode
    ? ['/RepairLookup/RepairScans', barcode]
    : null, (url, barcodeParam) => repairScanFetcher(url, barcodeParam),
  {
    refreshInterval: 1,
  });

  const { data: boxInfo } = useSWR(repairId ? ['/BoxLookup/RepairBox', repairId] : null, (url, repairIdParam) => boxFetcher(url, repairIdParam));
  const { data: boxScans } = useSWR(boxInfo?.trackingNumber
    ? ['/BoxLookup/BoxScans', boxInfo?.trackingNumber]
    : null, (url, trackNumParam) => boxScanFetcher(url, trackNumParam),
  {
    refreshInterval: 1,
  });

  const repairScanTableRows = repairScans && repairScans?.map((scan, index) => ({
    id: index || '',
    scanBody: scan.scanBody,
    scanType: scan.scanType,
    scanLocation: scan.scanLocation,
    created: new Date(`${scan.created}z`).toLocaleString(),
    scannedBy: scan.scannedBy,
  }));

  let bannerBuffer = '';
  let honeywellBuffer = '';

  const handleBannerInput = (e: KeyboardEvent) => {
    if (e.key !== 'Shift') {
      bannerBuffer = `${bannerBuffer}${e.key}`;
      if (bannerBuffer.includes('In:')) {
        bannerBuffer = '';
      }
      if (bannerBuffer.includes('{*|}')) {
        openRepair(bannerBuffer.replace('{*|}', ''));
        bannerBuffer = '';
        honeywellBuffer = '';
      }
    }
  };

  const handleHoneywellInput = (e: KeyboardEvent) => {
    if (e.key && (e.key.length === 1 || e.key === 'Enter')) {
      if (e.key !== 'Enter') {
        honeywellBuffer = `${honeywellBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented) {
          honeywellBuffer.replace('{*|}', '');
          openRepair(honeywellBuffer.replace('{*|}', ''));
          honeywellBuffer = '';
        }
        bannerBuffer = '';
        honeywellBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBannerInput);
  useEventListener('keydown', handleHoneywellInput);

  const RepairItemMap = (
    data?.repairItems?.map((repairItem:IRepairItem) => (
      <Grid item xs={12}>
        <Card>
          <Box ml={2} my={2}>
            <Typography variant="h5" style={{ fontSize: '1.125rem', color: '#587B9F' }}>{repairItem?.label || repairItem?.jewelryType?.label || repairItem?.repairItemType?.label}</Typography>
          </Box>
          <Divider />
          <CardContent style={{ backgroundColor: '#F5F5F5' }}>
            <Grid container spacing={0}>
              {
                repairItem?.repairItemJobs && repairItem?.repairItemJobs?.length >= 1
                && (
                <>
                  <Grid item xs={12}>
                    <Box>
                      <RepairJobContainer title="Work Assigned" repairItem={repairItem} />
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box p={2} />
                  </Grid>
                </>
                )
              }
              <Grid item xs={12}>
                <Box>
                  <RepairInformation RepairItem={repairItem} showExtraProps />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box p={2} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

      </Grid>
    ))
  );

  const boxScanTableColumns: GridColumns = [
    {
      field: 'scanBody',
      headerName: 'Tracking Number',
      flex: 0.75,
    },
    {
      field: 'scanLocation',
      headerName: 'Scan Location',
      flex: 1,
    },
    {
      field: 'created',
      headerName: 'Created',
      flex: 1,
    },
    {
      field: 'scannedBy',
      headerName: 'Scanned By',
      flex: 1,
    },
    {
      field: 'barcodeActions',
      type: 'string',
      headerName: 'Actions',
      flex: 0.75,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      valueGetter: ({ row }) => row.scanBody as string,
      renderCell: ({ value }) => (
        ((typeof (value) === 'string')
          ? displayVertIcon(value)
          : <></>
        )
      ),
    },
  ];

  const boxScanTableRows = boxScans && boxScans?.map((scan, index) => ({
    id: index || '',
    scanBody: scan.scanBody,
    scanLocation: scan.scanLocation,
    created: new Date(`${scan.created}z`).toLocaleString(),
    scannedBy: scan.scannedBy,
  }));

  const BoxHistory = (
    <Grid item xs={12}>
      <Paper>
        <Card>
          <CardHeader
            title={(
              <Typography variant="h6" display="inline">{'Box Scan History'}</Typography>
            )}
            disableTypography
          />
          <CardContent>
            <DataGrid
              rows={boxScanTableRows || []}
              columns={boxScanTableColumns || []}
              disableColumnMenu
              autoHeight
              pageSize={50}
            />
            {
              boxLinkBarcode && anchorEl && (
                <ActionMenu
                  anchorEl={anchorEl}
                  handleClose={handleClose}
                >
                  <MenuItem
                    id="test-button"
                    onClick={() => navigate(`/box-detail/${boxLinkBarcode}`)}
                  >
                    {'View Box Detail'}
                  </MenuItem>
                </ActionMenu>
              )
            }
          </CardContent>
        </Card>
      </Paper>
    </Grid>
  );

  if (!data) {
    return (
      <Grid container direction="column" spacing={2} alignItems="center" justifyContent="center">
        <Grid item>
          <Alert severity="info">{'Scan in repair to review marking data, estimation data, and images of repair.'}</Alert>
        </Grid>
        <Grid item>
          {openRepairProcessing
            ? (
              <CircularProgress />
            )
            : (
              <TextField
                variant="outlined"
                size="small"
                onKeyPress={
                  (event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      event.stopPropagation();
                      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                      openRepair((event.target as HTMLInputElement).value);
                    }
                  }
                  }
              />
            )}
        </Grid>
      </Grid>
    );
  }

  return (
    <Box>
      <Grid container spacing={0}>
        <Grid container item spacing={1} sm={12} md={9}>
          <Grid container item spacing={2} sm={12} md={12}>
            <Grid item xs={12}>
              <Alert
                severity="info"
              >
                {'Review repair information, job information, and images of repair to determine if work is done correctly.'}
              </Alert>
            </Grid>
            <Grid item xs={12}>
              <RepairCard
                barcode={barcode || ''}
                secondaryText={
                  `${data.repairItems?.length} item${data.repairItems?.length === 1 ? '' : 's'}`
                }
              />
            </Grid>
            <Grid item xs={12} />
            {RepairItemMap}
            <Grid item xs={12}>
              <Paper>
                <Card>
                  <CardHeader
                    title={(
                      <Typography variant="h6" display="inline">{'Repair Scan History'}</Typography>
                    )}
                    disableTypography
                  />
                  <CardContent>
                    <DataGrid
                      rows={repairScanTableRows || []}
                      columns={repairScanTableColumns}
                      disableColumnMenu
                      autoHeight
                      pageSize={50}
                    />
                  </CardContent>
                </Card>
              </Paper>
            </Grid>
            {BoxHistory}
            <RepairRejectLog barcode={barcode || ''} />
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <Divider orientation="vertical" />
      </Grid>
    </Box>
  );
};

export default Packer;
